import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { HaloLoaderService } from 'src/app/core/halo-loader/halo-loader.service';
import { HaloModalSize } from 'src/app/core/halo-modal/halo-modal-interface';
import { HaloModalService } from 'src/app/core/halo-modal/halo-modal.service';
import { EToastType, HaloToastService } from 'src/app/core/halo-toast/halo-toast.service';
import { AmplitudeAnalyticsService } from 'src/app/core/services/amplitude-analytics.service';
import { CommonUtil } from 'src/app/core/Utils/common-util';
import { SelectionInfo } from 'src/app/post-login/selectors/multi-selection/multi-selection.model';
import { environment } from 'src/environments/environment';
import { ProductSettingsService } from '../product-settings.service';
import { AssetManagerConfig, LanguageSettings, ProductSettings } from '../setting.model';

@Component({
  selector: 'app-general-product-settings',
  templateUrl: './general-product-settings.component.html',
  styleUrls: ['./general-product-settings.component.scss']
})
export class GeneralProductSettingsComponent implements OnInit, OnChanges {
  private hasEditPermission: boolean = true;
  private hasNewItem = false;
  private saveAsError = false;
  private originalData: ProductSettings;
  private settingsData: AssetManagerConfig[] = [];
  private selectedSetting: AssetManagerConfig | undefined = undefined;
  private showDeleteMsg = false;
  private hours: number[] = [];
  private hours24: number[] = [];
  private minutes: number[] = [];
  private saveAsConfigName: string;
  private NEW_SETTING_ID = -2;
  private multiBaseSupportConsoles = ['hercules', 'batman', 'robin'];
  private wirelessChargeOptions=
            [{id:'1', value:'On'},
            {id:'2', value:'Only While in Workout'},
            {id:'3', value:'Off'}];
  private deleteModalProps = {
              title: this.commonUtil.replaceValueAsHTMLOnTranslatedText('DELETE.NO', [this.commonUtil.translateText('DRAFT.CONFIRMATION.SETTING')]),
              message: 'DELETE.MODAL.MESSAGE',
              isWarningPopup: true,
              ctaButtonLabel: 'DELETE',
              negativeButtonLabel: 'CANCEL',
              isCloseIconCallsCta: false,
              isSecondaryTextButton: true,
              hideSecondaryButton: false,
            };
  languageConsoleList = ['aquaman','superman'];
  environment = environment;
  cloudBasePath: string;
  showLightingList = false;
  defaultLightingColor = 'blue';
  brightnessList :{id: number, value: string} [] = [{id: 0, value: 'Darker'}, {id: 1, value: 'Default'}, {id: 2, value: 'Brighter'}];
  @Input() selectionInfo: SelectionInfo;
  @Input() isSelectionChanged: boolean;

  @ViewChild('saveAsModalTemplate') saveAsModalTemplate: TemplateRef<any>;
  @ViewChild('saveAsModalActionTemplate') saveAsModalActionTemplate: TemplateRef<any>;

  constructor(
    private settingsService: ProductSettingsService,
    private modal: HaloModalService,
    private commonUtil: CommonUtil,
    private loader: HaloLoaderService,
    private toast: HaloToastService,
    private _amplitudeAnalytics: AmplitudeAnalyticsService
  ) { }

  ngOnInit(): void {
    this.cloudBasePath = environment.cloudBasePath; 
    this.prepareTimeArrays();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isSelectionChanged?.currentValue){
        this.hasEditPermission = this.selectionInfo.facility.summary?.isLoggedInManagerAdmin === 'true' ||
        this.selectionInfo.facility.summary?.userRights.customize === 'F';
        this.getData();
        this.hasNewItem = false;
    }
  }

  private prepareTimeArrays() {
    for (let index = 0; index < 60; index++) {
      if(index > 0 && index < 13) {
        this.hours.push(index);
      }
      if(index < 24) {
        this.hours24.push(index);
      }
      this.minutes.push(index);
    }
  }
  
  private async getData() {
    try {
      this.loader.startProcess();
      this.selectedSetting = undefined;
      this.originalData = await this.settingsService.getGeneralProductSettings([this.selectionInfo.facility.facilityId], this.selectionInfo.consoleType) as ProductSettings;
      if(this.originalData && this.originalData.productSettingsList) {
        this.originalData.productSettingsList.forEach(setting => this.postProcessSetting(setting));                     
        this.settingsData = JSON.parse(JSON.stringify(this.originalData.productSettingsList)); // deep copy
      }
      this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = false;
    } catch (error) {
      console.error("API error.")
    }
    finally {
      this.loader.endProcess();
      this.commonUtil.scrollTop();
    }
  }

  private postProcessSetting(setting: AssetManagerConfig) {
    this.updateIllustraLightingSetting(setting);
    this.processLanguageList(setting);
    this.selectLanguage(setting);
    this.checkTime(setting);
  }

  private updateIllustraLightingSetting(setting: AssetManagerConfig){
    if (this.selectionInfo.consoleType=='everest') {
      setting.lighting = setting.lighting === 'false' || !setting.lighting ? false: true;
      setting.lightingColor = setting.lightingColor ? setting.lightingColor: this.defaultLightingColor;
      const brightness = setting.brightness === 'darker' ? 0 : setting.brightness === 'default' ? 1 : 2;
      if(brightness !== Number(setting.tempData?.brightness)) {
        if(setting.tempData){
          setting.tempData.brightness = setting.brightness === 'darker' ? 0 : setting.brightness === 'default' ? 1 : 2;
          setting.tempData.lightingColors = this.updateBrightnessColors(Number(setting.tempData?.brightness));
        }         
      }
    }
  }

  private processLanguageList(setting: AssetManagerConfig) {
    if(setting.languageList?.length > 0) {
      setting.languageList.forEach(l => {
        let lang = this.originalData.configLanguages?.find(cl => cl.langCode === l.langCode);
        l.displayName = lang ? lang.name : l.langCode;
      });
    }
    if(setting.tempData){
      setting.tempData.selectedLanguageCount = setting.languageList.filter(((sVal:any) => sVal.isEnabled)).length;
    }    
  }

  private checkTime(setting: AssetManagerConfig) {
    if(setting.tempData?.autoOffHour && setting.tempData?.autoOffHour > 12) {
      setting.tempData.autoOffHour -= 12; 
    }

    if(setting.tempData?.autoOnHour && setting.tempData?.autoOnHour > 12) {
      setting.tempData.autoOnHour -= 12; 
    }

  }

  getDisplayColor(setting: any): string {
    return setting?.tempData && setting.tempData.lightingColors ? setting.tempData.lightingColors.find((colorItem:any) => colorItem.value === setting.lightingColor)?.displayColor ?? '' : '';
  }

  getColorName(setting: any): string {
    return setting.tempData && setting.tempData.lightingColors ? setting.tempData.lightingColors.find((colorItem:any) => colorItem.value === setting.lightingColor)?.name ?? '' : '';
  }

  onSelectedBrightness(setting: any) {
    setting.tempData.lightingColors = this.updateBrightnessColors(Number(setting.tempData.brightness));
    this.onEdit(setting);
  }

  updateBrightnessColors(brightness: number) {
    return [
      { color: brightness === 0 ? '#15120E': brightness === 1 ? '#423C2C' : '#E1C896', value: 'white', name: this.commonUtil.translateText('LIGHTING.COLOR.WHITE'), displayColor: '#F2F2F2' },
      { color: brightness === 0 ? '#080000': brightness === 1 ? '#3C0000' : '#C80000', value: 'red', name: this.commonUtil.translateText('LIGHTING.COLOR.RED'), displayColor: '#F00024' },
      { color: brightness === 0 ? '#120200': brightness === 1 ? '#3C0600' : '#C81400', value: 'mango', name: this.commonUtil.translateText('LIGHTING.COLOR.MANGO'), displayColor: '#FB5C00' },
      { color: brightness === 0 ? '#120400': brightness === 1 ? '#3C0C00' : '#C82800', value: 'orange', name: this.commonUtil.translateText('LIGHTING.COLOR.ORANGE'), displayColor: '#FB8400' },
      { color: brightness === 0 ? '#120700': brightness === 1 ? '#3C1800' : '#C85000', value: 'gold', name: this.commonUtil.translateText('LIGHTING.COLOR.GOLD'), displayColor: '#FCB700' },
      { color: brightness === 0 ? '#120800': brightness === 1 ? '#3C2300' : '#C87800', value: 'yellow', name: this.commonUtil.translateText('LIGHTING.COLOR.YELLOW'), displayColor: '#F6FB00' },
      { color: brightness === 0 ? '#071200': brightness === 1 ? '#183C00' : '#50C800', value: 'lime', name: this.commonUtil.translateText('LIGHTING.COLOR.LIME'), displayColor: '#9EFB00' },
      { color: brightness === 0 ? '#001200': brightness === 1 ? '#003C00' : '#00C800', value: 'green', name: this.commonUtil.translateText('LIGHTING.COLOR.GREEN'), displayColor: '#00FC50' },
      { color: brightness === 0 ? '#051203': brightness === 1 ? '#123C09' : '#3CC81E', value: 'mint', name: this.commonUtil.translateText('LIGHTING.COLOR.MINT'), displayColor: '#00FB9E' },
      { color: brightness === 0 ? '#021205': brightness === 1 ? '#063C0F' : '#14C832', value: 'aquamarine', name: this.commonUtil.translateText('LIGHTING.COLOR.AQUAMARINE'), displayColor: '#00FB9E' },
      { color: brightness === 0 ? '#001209': brightness === 1 ? '#003C1E' : '#00C864', value: 'teal', name: this.commonUtil.translateText('LIGHTING.COLOR.TEAL'), displayColor: '#00F0FD' },
      { color: brightness === 0 ? '#000712': brightness === 1 ? '#00183C' : '#0050C8', value: 'sky', name: this.commonUtil.translateText('LIGHTING.COLOR.SKY'), displayColor: '#0194D9' },
      { color: brightness === 0 ? '#000012': brightness === 1 ? '#00003C' : '#0000C8', value: 'blue', name: this.commonUtil.translateText('LIGHTING.COLOR.BLUE'), displayColor: '#2A4EFD' },
      { color: brightness === 0 ? '#070012': brightness === 1 ? '#16003C' : '#4B00C8', value: 'violet', name: this.commonUtil.translateText('LIGHTING.COLOR.VIOLET'), displayColor: '#9321FB' },
      { color: brightness === 0 ? '#0C0012': brightness === 1 ? '#1C003C' : '#8200C8', value: 'magenta', name: this.commonUtil.translateText('LIGHTING.COLOR.MAGENTA'), displayColor: '#DB21FC' },
      { color: brightness === 0 ? '#120004': brightness === 1 ? '#3C000C' : '#C80028', value: 'rose', name: this.commonUtil.translateText('LIGHTING.COLOR.ROSE'), displayColor: '#FC0093' },
    ]
  }

  private selectLanguage(setting: AssetManagerConfig) {
    if(!setting?.tempData) return;
    if(!setting.tempData.selectedConfigLanguage) {
      if(setting.languageList?.length > 0) {
        setting.tempData.selectedConfigLanguage = setting.languageList.find(l => l.isDefault)?.langCode ?? setting.languageList[0].langCode;
      }
    }
    //@ts-ignore
    let index = setting.languageList?.findIndex(l => l.langCode === setting.tempData.selectedConfigLanguage);
    if(index && index != -1) {
      setting.languageList = setting.languageList.map(lan => ( {...lan, isDefault: false}))
      let language = setting.languageList[index];
      language.isDefault = true;
      language.isEnabled = true;
      setting.languageList.splice(index, 1);
      setting.languageList.unshift(language);
      setting.tempData.selectedLanguageCount = setting.languageList.filter(l => l.isEnabled)?.length ?? 0;
    }
  }

  private onChangeAvailableLanguage(setting: AssetManagerConfig, language: LanguageSettings) {
    if(!setting?.tempData) return;
    let index = setting.languageList.findIndex(l => l.langCode == language.langCode);
    // ignore first index as it is the current selected
    if(index > 0) {
      language.isEnabled = !language.isEnabled;
      setting.tempData.selectedLanguageCount = setting.languageList.filter(l => l.isEnabled)?.length ?? 0;
    }
    this.onEdit(setting);
  }

  private onToggleCollapse(event: any, setting: AssetManagerConfig) {
    this.selectedSetting = setting;
    var expanded = event.target.getAttribute('aria-expanded');
    if(expanded === 'true'){
      this.selectedSetting = undefined;
    }
  }

  private onShutOffTimeChange(setting: AssetManagerConfig) {
    if(!setting?.tempData) return;
    setting.autoOnTime = this.toMinutes(setting.tempData.autoOnHour, setting.tempData.autoOnMinute, setting.tempData.autoOnUnit == 'AM');
    setting.autoOffTime = this.toMinutes(setting.tempData.autoOffHour, setting.tempData.autoOffMinute, setting.tempData.autoOffUnit == 'AM');
    setting.tempData.autoOnError = setting.autoOnTime == setting.autoOffTime;
    this.setErrorFlag(setting);
    this.onEdit(setting);
  }
  
  private addNew() {
    if(this.hasNewItem){
      return;
    }

    let newProductSetting = this.getNewProductSetting();
    if(newProductSetting){
      if(newProductSetting.tempData.brightness !== 1) {
        newProductSetting.tempData.brightness = 1;
        newProductSetting.tempData.lightingColors = this.updateBrightnessColors(newProductSetting.tempData.brightness);
      }
      this.selectedSetting = newProductSetting;
      this.settingsData.unshift(newProductSetting);
      this.showSelectedSetting();
    }

    this.hasNewItem = true;
    this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = true;
  }

  getNewProductSetting(){
    let newProductSetting;
    let defaultSetting = this.settingsData.find(s => s.managerId==0);
    if(defaultSetting) {
      let newSetting = JSON.parse(JSON.stringify(defaultSetting));
      delete newSetting['managerId'];
			delete newSetting['inUse'];
			delete newSetting['updatedAt'];
			delete newSetting['updatedAtTimeStamp'];

      var datePipe = new DatePipe('en-US');
      newSetting.configName = `${this.commonUtil.translateText('PRODUCT.SETTING')} ${datePipe.transform(Date.now(), 'MMddyyyy_H:mm')}`;
      newSetting.id = this.NEW_SETTING_ID;
      newSetting.isEdited = true;
      newProductSetting = newSetting;
    }
    return newProductSetting;
  }

  private onEdit(setting: AssetManagerConfig) {
    if(!setting?.tempData) return;
    setting.tempData.isEdited = true;
    this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = true;
  }

  onLighting(setting: AssetManagerConfig, value: string): void {
    setting.lighting = value;
    if (value === 'true' && !setting.lightingColor) {
      setting.lightingColor = this.defaultLightingColor;
    }
  }

  private undo(setting: AssetManagerConfig, index: number) {
    if(setting.id !== -2) {
      let oldSetting = this.originalData.productSettingsList.find(s => s.id == setting.id);
      if (this.selectionInfo.consoleType == 'everest' && oldSetting) { 
        oldSetting.lighting = oldSetting.lighting === 'false' || !oldSetting.lighting ? false: true
      }
      this.settingsData[index] = JSON.parse(JSON.stringify(oldSetting)); // assign back the original data
    }else{
      let newProductSetting = this.getNewProductSetting();
      if(newProductSetting){
        if(newProductSetting.tempData.brightness !== 1) {
          newProductSetting.tempData.brightness = 1;
          newProductSetting.tempData.lightingColors = this.updateBrightnessColors(newProductSetting.tempData.brightness);
        }
        this.settingsData[index] = JSON.parse(JSON.stringify(newProductSetting)); // assign back the original data
      }
    }
    this.selectedSetting = this.settingsData[index];
    this.showSelectedSetting();
    this.checkUnsaveData();
  }

  private showSaveAs() {
    if(!this.selectedSetting) return;
    this.hasNewItem = false;
    this.saveAsError = false;
    this.showSaveAsModalDialog();
  }

  showSaveAsModalDialog() {
    var datePipe = new DatePipe('en-US');
    this.saveAsConfigName = `${this.commonUtil.translateText('PRODUCT.SETTING')} ${datePipe.transform(Date.now(), 'MMddyyyy_H:mm')}`;
    this.modal.add({
      dismissible: true,
      titleUppercase: true,      
      modalSize: HaloModalSize.Medium,
      title: this.commonUtil.translateText('SAVEAS'),
      bodyTemplate: this.saveAsModalTemplate,
      footerTemplate: this.saveAsModalActionTemplate
    });
  }

  private closeSaveAs() {
    this.modal.close();
  }

  private saveAsSetting() {
    if(this.saveAsConfigName.trim() === ''){
      this.saveAsError = true;
    }else{
      if(!this.selectedSetting) return;
      this.saveAsError = false;
      let saveAsData = JSON.parse(JSON.stringify(this.selectedSetting)) as AssetManagerConfig;
      saveAsData.id = this.NEW_SETTING_ID;
      saveAsData.configName = this.saveAsConfigName;
      let indexToSave = 0;
      // check for draft item
      if(this.settingsData[0].id == this.NEW_SETTING_ID) {
        indexToSave = 1; // insert to second index
      }
      this.settingsData.splice(indexToSave, 0, saveAsData); 
      this.save(saveAsData, indexToSave);
      this.closeSaveAs();
    }
   
  }

  private async save(setting: AssetManagerConfig, index: number) {
    if(!setting) return;
    let settingToSave = this.prepareDataToSave(setting);
    if(!settingToSave) return;

    try {
      this.loader.startProcess();
      let response = await this.settingsService.saveGeneralProductSettings({assetMgrConfig: settingToSave});
      if (this.selectionInfo.consoleType == 'everest') {
        response.lighting = response.lighting === 'false' || !response.lighting ? false: true
       }
      this.postProcessSetting(response);
      let copy = JSON.parse(JSON.stringify(response));
      // for new data, insert at the start of originalData
      if(setting.id == this.NEW_SETTING_ID) {
        this.originalData.productSettingsList.unshift(copy);
      } else { //replace the old setting
        let idx = this.originalData.productSettingsList.findIndex(s => s.id == setting.id)
        if(idx > -1) this.originalData.productSettingsList[idx] = copy;
      } 
      //Update selected language count.
      response.tempData.selectedLanguageCount = response.languageList.filter(((sVal:any) => sVal.isEnabled)).length;
      response.tempData.brightness = response.brightness === 'darker' ? 0 : response.brightness === 'default' ? 1 : 2;
      response.tempData.lightingColors = this.updateBrightnessColors(Number(response.tempData?.brightness));
      this.settingsData[index] = response;
      this.selectedSetting = response;      
      this.hasNewItem = false;
      this.toast.creatToast(this.commonUtil.replaceValueAsHTMLOnTranslatedText('GENERIC.SAVED.SUCCESSFULLY', ['<b>'+settingToSave.configName+'</b>']), EToastType.SUCCESS);
      this.showSelectedSetting();
      this.checkUnsaveData();
    } catch (error) {
      console.error("API error.")
    }
    finally {
      this.loader.endProcess();
    }
  }

  private prepareDataToPublish(setting:any){
    let preparedData = setting;
    preparedData.displayLoginQRCode = String(preparedData.displayLoginQRCode);
    preparedData.isLifescapeHomeEnabled = String(preparedData.isLifescapeHomeEnabled);
    return preparedData;
  }


  private async publish(setting: AssetManagerConfig) {
    if(!setting?.tempData) return;
    let settingToSave = this.prepareDataToSave(setting);
    let settingToPublish = this.prepareDataToPublish(settingToSave);
    if(!settingToPublish) return;
    if(settingToPublish.managerId == 0) settingToPublish.id = -2;
    let postData = {
      assetMgrConfigVO: settingToPublish,
      consoleType: this.selectionInfo.consoleType,
      facilityIds: [this.selectionInfo.facility.facilityId],
      activityIds: [this.selectionInfo.activityId]
    }

    try {
      this.loader.startProcess();
      await this.settingsService.publishGeneralProductSettings(postData);
      this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = false;
      this.toast.creatToast(this.commonUtil.replaceValueAsHTMLOnTranslatedText('GENERIC.PUBLISHED.SUCCESSFULLY', ['<b>'+settingToPublish.configName+'</b>']), EToastType.SUCCESS);
      this.getData();
    } catch (error) {
      console.error("API error.")
    }
    finally {
      this.hasNewItem = false;
      this.loader.endProcess();
    }
  }

  private checkUnsaveData() {
    //@ts-ignore
    this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = !!this.settingsData.find(s => s.tempData.isEdited);
  }

  private prepareDataToSave(setting: AssetManagerConfig) {
    if(!setting?.tempData) return;
    let languageList = JSON.parse(JSON.stringify(setting.languageList)) ;

    languageList.forEach((val:any) => {
      val.isDefault = String(val.isDefault);
      val.isEnabled = String(val.isEnabled);
    });
    const settingToSave: any = {
      "id" : setting.id == this.NEW_SETTING_ID ? null : (setting.managerId === 0?this.NEW_SETTING_ID:setting.id),
      "consoleType" : this.selectionInfo.consoleType,
      "configName" : setting.configName,
      "displayUnits" : Number(setting.displayUnits),
      "displayLoginQRCode" : setting.displayLoginQRCode?setting.displayLoginQRCode:'',
      "isAudioFeedbackEnabled" : String(setting.isAudioFeedbackEnabled),
      "volume" : setting.volume,
      "isAutoOnOffEnabled" : String(setting.isAutoOnOffEnabled),
      "isBluetoothEnabled" : String(setting.isBluetoothEnabled),
      "autoOffTime": setting.autoOffTime,
      "autoOnTime": setting.autoOnTime,
      "isLifescapeHomeEnabled" : setting.isLifescapeHomeEnabled?setting.isLifescapeHomeEnabled:'',
      "isInactivityTimerEnabled" : String(setting.isInactivityTimerEnabled),
      "inactivityDuration": this.toMinutes(setting.tempData.inactivityHour, setting.tempData.inactivityMinute, true),
      "inetStatus" : setting.inetStatus,
      "isGoogleSearchEnabled" : setting.isGoogleSearchEnabled,
      "isInetUserIdReq" : setting.isInetUserIdReq,
      "isIpodEnabled" : setting.isIpodEnabled,
      "isIpodVideoEnabled" : setting.isIpodVideoEnabled,
      "isFmEnabled" : setting.isFmEnabled,
      "isProductAppsEnabled" : setting.isProductAppsEnabled,
      "isMobileAppsEnabled" : setting.isMobileAppsEnabled,
      "appList" : setting.appList,
      "isTVEnabled": setting.isTVEnabled,
      "isCustomMessageInWODisplayed": String(setting.isCustomMessageInWODisplayed),
      "languageList" : languageList,
      "wirelessCharging": setting.wirelessCharging  
    };
  
    if (this.selectionInfo.consoleType == 'everest') {
      settingToSave.lighting = setting.lighting;
      if(setting.lighting == true) {
        settingToSave.lightingColor = setting.lightingColor;
        settingToSave.brightness = Number(setting.tempData.brightness) === 0 ? 'darker' : Number(setting.tempData.brightness) === 1 ? 'default' : 'brighter';
      }
    }

    if(this.selectionInfo.consoleType == 'hercules'){
      settingToSave.systemBrightness = setting.systemBrightness;
      settingToSave.isNetworkEnabled = setting.isNetworkEnabled;
    } else {
      settingToSave.networkId = setting.networkId;
    }
     
    if(this.selectionInfo.consoleType =='aquaman' && 
      this.selectionInfo.facility.summary.isPlanetFitness =='true'){
      settingToSave.systemBrightness = setting.systemBrightness;
    }	
    
    if((this.selectionInfo.consoleType=="aquaman" || this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman') && 
      this.selectionInfo.facility.summary.staffPermissionData.haloMediaOptedIn=='true'){
      settingToSave.isInactivityTimerEnabled = "false";
    }

    return settingToSave;
  }

  private toMinutes(hours?: number, minutes?: number, isAM?: boolean) {
    if(Number(hours) === 12) {
      hours = 0;
    }
    return (Number(hours) + (isAM?0:12)) * 60 + Number(minutes);
  }

  private showSelectedSetting() {
    if(!this.selectedSetting) return;
    setTimeout(() => {
      //@ts-ignore
      $(`#collapse${this.selectedSetting.id}`).collapse('show');
    }, 10);
  }

  private showDeleteModal() {
    if(!this.selectedSetting) return;
    this.showDeleteMsg = true;
    this.deleteModalProps.message = this.selectedSetting.inUse ? 
      this.commonUtil.replaceValueAsHTMLOnTranslatedText('DELETE.PUBLISHED.MODAL.MESSAGE', [this.commonUtil.translateText('DRAFT.CONFIRMATION.SETTING')]) :
      'DELETE.MODAL.MESSAGE';
  }

  private async confirmDelete() {
    if(!this.selectedSetting) return;
    if(this.selectedSetting.id !== this.NEW_SETTING_ID) {
      try {
        this.loader.startProcess();
        this._amplitudeAnalytics.logEvent({elementName: 'Delete', action: "Click"});
        await this.settingsService.deleteGeneralProductSettings(this.selectedSetting.id as number, this.selectionInfo.consoleType);        
        // reload data when published setting is deleted
        if(this.selectedSetting.inUse){
          this.getData();
        } else {
          this.checkUnsaveData();          
        }
      } catch (error) {  
        console.error('API error.');
      }
      finally {
        this.loader.endProcess();
      }
    } 
    this.settingsData = this.settingsData.filter(s => s.id != this.selectedSetting?.id);
    this.selectedSetting = undefined;  
    this.showDeleteMsg = false;
    this.hasNewItem = false;
    this.commonUtil.scrollTop();
  }

  private setErrorFlag(setting: AssetManagerConfig) {
    if(!setting?.tempData) return;
    setting.tempData.hasError = setting.tempData.autoOnError;
  }
}
